import { toast } from "../toast/toast";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  Lab,
  ReagentCategory,
  ReagentGroup,
  ReagentSize,
  Unit,
} from "../../../backend/src/types";
import { useUser } from "../contexts/UserContext";
import {
  addReagentGroup,
  checkReagentGroupUsed,
  editReagentGroup,
  getReagentSizeList,
} from "../fetch/ReagentGroupRequest";
import styles from "../styles/modules/AdminPage.module.css";
import RequiredMark from "./RequiredMark";

// CAS登録番号の標準形式（例：64-17-5）。数字2〜7桁-数字2桁-数字1桁。
const CAS_NUMBER_REGEX = /^\d{2,7}-\d{2}-\d$/;
const CAS_NUMBER_ERROR = "CAS登録番号の形式が正しくありません。（例：64-17-5）";

interface ReagentGroupPopupProps {
  targetReagentGroup: ReagentGroup | null;
  reagentCategories: ReagentCategory[];
  selectedLab: Lab | null;
  units: Unit[];
  onClose: () => void;
  onLoading: (start: boolean) => void;
  updateReagentGroupList: (updateReagentGroup: ReagentGroup) => void;
}

const ReagentGroupPopup: React.FC<ReagentGroupPopupProps> = ({
  targetReagentGroup,
  reagentCategories,
  selectedLab,
  units,
  onClose,
  onLoading,
  updateReagentGroupList,
}) => {
  const [reagentSizes, setReagentSizes] = useState<ReagentSize[]>([]);
  const [selectedReagentCategory, setSelectedReagentCategory] =
    useState<number>(0);
  const [selectedUnit, setSelectedUnit] = useState<string>("");
  const [oldName, setOldName] = useState<string>("");
  const [oldUnit, setOldUnit] = useState<string>("");
  // このマスターを参照している実際の試薬件数。1件以上なら単位変更を禁止する。
  const [usedCount, setUsedCount] = useState<number>(0);
  const isEdit = targetReagentGroup !== null;
  const nameRef = useRef<HTMLInputElement>(null);
  const casRef = useRef<HTMLInputElement>(null);

  const capacityBaseRef = useRef<HTMLInputElement>(null);
  const reagentSizeUnitRef = useRef<HTMLSelectElement>(null);

  const { user, memberships } = useUser();

  useEffect(() => {
    if (!user || !selectedLab) {
      return;
    }

    if (targetReagentGroup) InitEditModeFrom(targetReagentGroup);
  }, [user, memberships, targetReagentGroup]);

  useEffect(() => {
    if (isEdit && targetReagentGroup && units.length > 0) {
      setSelectedUnit(targetReagentGroup.total_unit ?? "");
      setSelectedReagentCategory(targetReagentGroup.category_id);
    }
  }, [isEdit, targetReagentGroup, units]);

  function InitEditModeFrom(targetReagentGroup: ReagentGroup) {
    setOldUnit(targetReagentGroup.total_unit);
    if (nameRef.current) {
      nameRef.current.value = targetReagentGroup.name;
      setOldName(targetReagentGroup.name);
    }
    if (casRef.current && targetReagentGroup.cas_number) {
      casRef.current.value = targetReagentGroup.cas_number;
    }
    loadReagentSizeList();
    loadUsedCount();
  }

  // このマスターを使用している試薬件数を取得する（単位ロック判定用）。
  async function loadUsedCount() {
    const count = await checkReagentGroupUsed({
      group_id: targetReagentGroup?.id ?? 0,
      tenant_id: user?.tenant_id ?? 0,
    });
    setUsedCount(count > 0 ? count : 0);
  }

  async function loadReagentSizeList() {
    onLoading(true);

    const reagentSizeList = await getReagentSizeList({
      group_id: targetReagentGroup?.id ?? 0,
    });

    if (reagentSizeList != null && reagentSizeList.length > 0) {
      setReagentSizes(reagentSizeList);
    }

    onLoading(false);
  }

  // 上部の単位（total_unit）変更時の処理。
  // 単位の種類（mass/volume/count）が変わったら、既存のサイズは種類が
  // 合わなくなるためクリアし、サイズ単位の入力もリセットして混在を防ぐ。
  function handleChangeTotalUnit(newUnit: string) {
    const prevKind = units.find((u) => u.code === selectedUnit)?.kind;
    const nextKind = units.find((u) => u.code === newUnit)?.kind;
    setSelectedUnit(newUnit);
    if (prevKind !== nextKind) {
      setReagentSizes([]);
      if (reagentSizeUnitRef.current) {
        reagentSizeUnitRef.current.value = "";
      }
    }
  }

  async function handleAddReagentGroup() {
    const name = nameRef.current?.value.trim() ?? "";
    const cas_number = casRef.current?.value.trim() ?? "";
    const total_unit = selectedUnit;
    const category_id = selectedReagentCategory;

    if (name === "") {
      toast.error("名前が未入力または空白のみで構成されています。");
      return null;
    }

    if (cas_number !== "" && !CAS_NUMBER_REGEX.test(cas_number)) {
      toast.error(CAS_NUMBER_ERROR);
      return null;
    }

    if (category_id === 0 || total_unit === "") {
      toast.error("必須項目のいずれかが未選択です。");
      return;
    }

    const baseUnit = units.find((u) => u.code === selectedUnit);
    const baseKind = baseUnit?.kind;

    const mismatchUnits = reagentSizes.filter((size) => {
      const unit = units.find((u) => u.code === size.unit);
      return unit?.kind !== baseKind;
    });

    if (mismatchUnits.length > 0) {
      toast.error("基本単位と系統が異なる単位のサイズ設定が含まれています。");
      return;
    }

    const reagentGroup: ReagentGroup = {
      id: targetReagentGroup?.id ?? 0,
      tenant_id: user?.tenant_id ?? 0,
      lab_id: selectedLab?.id ?? 0,
      category_id,
      name,
      total_unit,
      total_min_qty_front: 0,
      cas_number,
      created_at: "",
      updated_at: "",
    };

    onLoading(true);

    const res = await addReagentGroup({
      reagentGroup,
      reagentSizes,
    });

    if (res != null) {
      updateReagentGroupList(res.group);
      toast.success(`${name}を登録しました。`);
      onClose();
    }
    onLoading(false);
  }

  async function handleEditReagentGroup() {
    const name = nameRef.current?.value.trim() ?? "";
    const cas_number = casRef.current?.value.trim() ?? "";
    const total_unit = selectedUnit;
    const category_id = selectedReagentCategory;

    if (name === "") {
      toast.error("名前が未入力または空白のみで構成されています。");
      return null;
    }

    if (cas_number !== "" && !CAS_NUMBER_REGEX.test(cas_number)) {
      toast.error(CAS_NUMBER_ERROR);
      return null;
    }

    if (category_id === 0 || total_unit === "") {
      toast.error("必須項目のいずれかが未選択です。");
      return;
    }

    const baseUnit = units.find((u) => u.code === selectedUnit);
    const baseKind = baseUnit?.kind;

    const mismatchUnits = reagentSizes.filter((size) => {
      const unit = units.find((u) => u.code === size.unit);
      return unit?.kind !== baseKind;
    });

    if (mismatchUnits.length > 0) {
      toast.error("基本単位と系統が異なる単位のサイズ設定が含まれています。");
      return;
    }

    const reagentGroup: ReagentGroup = {
      id: targetReagentGroup?.id ?? 0,
      tenant_id: user?.tenant_id ?? 0,
      lab_id: selectedLab?.id ?? 0,
      category_id,
      name,
      total_unit,
      total_min_qty_front: 0,
      cas_number,
      created_at: "",
      updated_at: "",
    };

    const isSameName = name === oldName;

    onLoading(true);

    const usedNum = await checkReagentGroupUsed({
      group_id: targetReagentGroup?.id ?? 0,
      tenant_id: user?.tenant_id ?? 0,
    });

    //基本単位を変更されると試薬詳細の総残量がおかしくなるので除外
    if (usedNum > 0 && oldUnit !== total_unit) {
      toast.error(
        `この試薬マスターは現在${usedNum}件の試薬で使用されているため、基本単位を変更できません。`,
      );
      onLoading(false);
      return;
    }

    const res = await editReagentGroup({
      reagentGroup,
      reagentSizes,
      isSameName,
    });

    if (res != null) {
      updateReagentGroupList(res.group);
      toast.success(`${name}を更新しました。`);
      onClose();
    }
    onLoading(false);
  }

  async function addReagentSize() {
    const capacity_baseStr = capacityBaseRef.current?.value ?? "";
    const unit = reagentSizeUnitRef.current?.value ?? "";

    if (capacity_baseStr === "" || unit === "") {
      toast.error("必須項目のいずれかが未選択です。");
      return;
    }

    const capacity_base = Number(capacity_baseStr);

    if (!Number.isInteger(capacity_base)) {
      toast.error("容量は半角数字のみ入力してください。");
      return;
    }

    if (capacity_base <= 0) {
      toast.error("容量は0を超える値を入力してください。");
      return;
    }

    const newReagentSize: ReagentSize = {
      //id,group_idは非nullを埋めるための一時的な値
      id: Date.now(),
      group_id: 0,
      capacity_base,
      unit,
    };

    //ifで非nullにして警告を黙らせる
    if (capacityBaseRef.current) capacityBaseRef.current.value = "";
    if (reagentSizeUnitRef.current) reagentSizeUnitRef.current.value = "";

    setReagentSizes((prev) => [...prev, newReagentSize]);
  }

  function deleteReagentSize(id: number) {
    setReagentSizes((prev) => prev.filter((m) => m.id !== id));
  }

  // サイズ単位は、上部で選んだ単位と同じ種類（mass/volume/count）のみに絞る。
  // 上部の単位が未選択のときは空にして「先に単位を選択」を促す。
  const selectedKind = units.find((u) => u.code === selectedUnit)?.kind;
  const sizeUnitOptions = selectedKind
    ? units.filter((u) => u.kind === selectedKind)
    : [];

  // 編集中かつ実際の試薬で使用されている場合は、単位変更を禁止する。
  const isUnitLocked = isEdit && usedCount > 0;

  const reagentGroupPopUpContent = (
    <div className={styles["popup-background"]} onClick={() => onClose()}>
      <div
        className={styles["popup-section"]}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{isEdit ? "試薬マスター情報の編集" : "試薬マスターの登録"}</h3>
        <form
          onKeyDown={(e) => {
            // 入力欄での Enter による暗黙送信を防ぐ（登録/更新はボタン押下のみ）
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
          onSubmit={(e) => {
            e.preventDefault();
            isEdit ? handleEditReagentGroup() : handleAddReagentGroup();
          }}
        >
          <label>
            <span>
              試薬マスター名
              <RequiredMark />
            </span>
            <input
              type="text"
              ref={nameRef}
              placeholder="例：エタノール"
              required
            />
          </label>
          <label>
            <span>CAS登録番号</span>
            <input
              type="text"
              ref={casRef}
              pattern="^\d{2,7}-\d{2}-\d$"
              title="CAS登録番号の形式で入力してください（例：64-17-5）"
              placeholder="例：64-17-5"
              onInput={(e) => {
                const input = e.currentTarget;
                input.value = input.value.replace(/[^0-9\-]/g, "");
              }}
            />
          </label>
          <label>
            <span>
              分類
              <RequiredMark />
            </span>
            <select
              value={selectedReagentCategory}
              onChange={(e) => {
                setSelectedReagentCategory(Number(e.target.value));
              }}
              required
            >
              <option value="">試薬分類を選択してください</option>
              {reagentCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>
              単位
              <RequiredMark />
            </span>
            <select
              required
              value={selectedUnit}
              onChange={(e) => handleChangeTotalUnit(e.target.value)}
              disabled={isUnitLocked}
              title={
                isUnitLocked
                  ? "使用中の試薬があるため単位は変更できません"
                  : undefined
              }
            >
              <option value="">単位</option>
              {units.map((unit) => (
                <option key={unit.code} value={unit.code}>
                  {unit.code}
                </option>
              ))}
            </select>
            {isUnitLocked && (
              <small className={styles["field-note"]}>
                この試薬マスターは{usedCount}件の試薬で使用中のため、単位は変更できません。
              </small>
            )}
          </label>
          <h3>サイズ設定</h3>
          <div>
            <div>
              <label>
                サイズ
                <div className={styles["unit-input"]}>
                  <input
                    type="number"
                    inputMode="numeric"
                    ref={capacityBaseRef}
                    min={0}
                  />
                  <select
                    ref={reagentSizeUnitRef}
                    defaultValue={
                      isEdit ? (targetReagentGroup?.total_unit ?? "") : ""
                    }
                    disabled={!selectedUnit}
                  >
                    <option value="">単位</option>
                    {sizeUnitOptions.map((unit) => (
                      <option key={unit.code} value={unit.code}>
                        {unit.code}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            </div>
            <button type="button" onClick={addReagentSize}>
              追加
            </button>
          </div>
          {reagentSizes.length === 0 ? (
            <p className={styles["reagentSize-none"]}>サイズ情報なし</p>
          ) : (
            <div className={`${styles["list-section"]} ${styles["up-space"]}`}>
              <table>
                <thead>
                  <tr>
                    <th>サイズ</th>
                    <th>削除</th>
                  </tr>
                </thead>
                <tbody>
                  {reagentSizes.map((reagentSize) => {
                    return (
                      <tr key={reagentSize.id}>
                        <td>
                          {reagentSize.capacity_base ?? "-"}
                          {reagentSize.unit ?? "-"}
                        </td>
                        <td>
                          <button
                            onClick={() => deleteReagentSize(reagentSize.id)}
                            type="button"
                          >
                            削除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className={styles["button-row"]}>
            <button className={styles["ok-button"]} type="submit">
              {isEdit ? "更新" : "登録"}
            </button>
            <button type="button" onClick={() => onClose()}>
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    reagentGroupPopUpContent,
    document.getElementById("popup-root")!,
  );
};

export default ReagentGroupPopup;
